import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, House, Buildings, Confetti, MusicNotes, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { ownerNav } from "@/config/nav";
import { ImageUploader } from "@/components/ImageUploader";
import { MapView } from "@/components/MapView";
import api, { fileUrl, formatINR, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = [
  { name: "Farmhouses", icon: House, desc: "Private stays for families & groups" },
  { name: "Resorts", icon: Buildings, desc: "Rooms, pools & event facilities" },
  { name: "Banquet Halls", icon: Confetti, desc: "Weddings & grand receptions" },
  { name: "Function Halls", icon: MusicNotes, desc: "Events, parties & functions" },
];

const AMENITIES_BY_CATEGORY = {
  Farmhouses: ["Bedrooms", "Bathrooms", "Swimming Pool", "AC", "Parking", "Kitchen", "Garden", "BBQ", "Indoor Games", "Outdoor Games", "Wi-Fi", "Security", "Power Backup"],
  Resorts: ["Rooms", "Swimming Pool", "AC", "Restaurant", "Parking", "Wi-Fi", "Breakfast", "Family Friendly", "Event Space", "Security", "Power Backup"],
  "Banquet Halls": ["AC", "Parking", "Food", "Music", "Decoration", "Stage", "Kitchen", "Rooms", "Event Space", "Security", "Power Backup"],
  "Function Halls": ["AC", "Parking", "Food", "Music", "Decoration", "Stage", "Kitchen", "Rooms", "Event Space", "Security", "Power Backup"],
};

const STEPS = ["Category", "Basic Info", "Capacity", "Pricing", "Amenities", "Rules", "Photos", "Preview", "Submit"];

export default function PropertyForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  const [step, setStep] = useState(1);
  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: "", category: "", description: "", location: "", address: "", city: "Hyderabad", district: "Ranga Reddy",
    state: "Telangana", maps_url: "", latitude: "", longitude: "", contact: "", max_guests: 50, seating_capacity: 0, dining_capacity: 0,
    price_starting: "", price_weekday: "", price_weekend: "", price_holiday: "", price_dayout: "", price_overnight: "",
    checkin: "12:00 PM", checkout: "11:00 AM", amenities: [], images: [],
    rules: { smoking: "Not Allowed", pets: "Not Allowed", parties: "Allowed", alcohol: "Allowed in Private Areas" },
  });

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data));
    if (editing) {
      api.get(`/properties/${id}`).then((r) => {
        const p = r.data;
        setF({
          name: p.name, category: p.category, description: p.description, location: p.location, address: p.address,
          city: p.city, district: p.district, state: p.state, maps_url: p.maps_url, latitude: p.latitude ?? "", longitude: p.longitude ?? "",
          contact: p.contact, max_guests: p.max_guests, seating_capacity: p.seating_capacity || 0, dining_capacity: p.dining_capacity || 0,
          price_starting: p.prices.starting, price_weekday: p.prices.weekday || "", price_weekend: p.prices.weekend, price_holiday: p.prices.holiday || "",
          price_dayout: p.prices.dayout, price_overnight: p.prices.overnight, checkin: p.checkin, checkout: p.checkout,
          amenities: p.amenities, images: p.images, rules: p.rules || {},
        });
        setStep(2);
      });
    }
  }, [id, editing]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });
  const setRule = (k) => (v) => setF({ ...f, rules: { ...f.rules, [k]: v } });
  const toggleAmenity = (a) => setF({ ...f, amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a] });

  const isHall = ["Banquet Halls", "Function Halls"].includes(f.category);
  const amenityList = AMENITIES_BY_CATEGORY[f.category] || AMENITIES_BY_CATEGORY.Farmhouses;

  const validateStep = () => {
    if (step === 1 && !f.category) return "Please select a category.";
    if (step === 2 && (!f.name || !f.description || !f.location || !f.contact)) return "Please fill name, description, location and contact.";
    if (step === 4 && !f.price_starting) return "Please enter a starting price.";
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) return toast.error(err);
    setStep((s) => Math.min(s + 1, 9));
  };
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const submit = async () => {
    setSaving(true);
    const num = (v) => (v === "" || v == null ? 0 : Number(v));
    const payload = {
      ...f, max_guests: num(f.max_guests), seating_capacity: num(f.seating_capacity), dining_capacity: num(f.dining_capacity),
      price_starting: num(f.price_starting), price_weekday: num(f.price_weekday), price_weekend: num(f.price_weekend),
      price_holiday: num(f.price_holiday), price_dayout: num(f.price_dayout), price_overnight: num(f.price_overnight),
      latitude: f.latitude === "" ? null : Number(f.latitude), longitude: f.longitude === "" ? null : Number(f.longitude),
      short_description: f.description.slice(0, 120),
    };
    try {
      if (editing) { await api.put(`/properties/${id}`, payload); toast.success("Property updated"); }
      else { await api.post("/properties", payload); toast.success("Property submitted for admin approval"); }
      navigate("/owner/properties");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const Field = ({ label, children }) => (<div><Label>{label}</Label><div className="mt-1">{children}</div></div>);

  return (
    <DashboardLayout nav={ownerNav} title="Owner">
      <button onClick={() => navigate("/owner/properties")} className="mb-3 flex items-center gap-1 text-sm text-stone-600"><ArrowLeft size={18} /> Back</button>
      <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">{editing ? "Edit Property" : "List a New Property"}</h1>

      {/* Stepper */}
      <div className="no-scrollbar mt-5 flex gap-2 overflow-x-auto pb-2">
        {STEPS.map((label, i) => {
          const n = i + 1;
          const active = n === step;
          const done = n < step;
          return (
            <button key={label} onClick={() => (editing || n < step) && setStep(n)} data-testid={`step-${n}`} className={`flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${active ? "bg-primary text-white" : done ? "bg-accent text-primary" : "bg-stone-100 text-stone-400"}`}>
              <span className={`flex h-5 w-5 items-center justify-center rounded-full ${active ? "bg-white/25" : done ? "bg-primary text-white" : "bg-stone-200"}`}>{done ? <Check size={12} weight="bold" /> : n}</span>
              {label}
            </button>
          );
        })}
      </div>

      <div className="mt-6 max-w-3xl rounded-2xl border border-stone-200 bg-white p-6">
        {step === 1 && (
          <div>
            <h2 className="mb-4 font-heading font-semibold">Select a category</h2>
            <div className="grid grid-cols-2 gap-3">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const sel = f.category === c.name;
                return (
                  <button key={c.name} type="button" data-testid={`cat-${c.name}`} onClick={() => setF({ ...f, category: c.name, amenities: [] })} className={`flex flex-col items-start gap-2 rounded-2xl border-2 p-4 text-left transition-colors ${sel ? "border-primary bg-accent" : "border-stone-200 hover:border-stone-300"}`}>
                    <Icon size={26} weight="duotone" className="text-primary" />
                    <span className="font-heading font-semibold text-stone-900">{c.name}</span>
                    <span className="text-xs text-stone-500">{c.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h2 className="font-heading font-semibold">Basic information</h2>
            <Field label="Property name"><Input data-testid="pf-name" value={f.name} onChange={set("name")} className="rounded-xl" /></Field>
            <Field label="Description"><Textarea data-testid="pf-description" value={f.description} onChange={set("description")} rows={4} className="rounded-xl" /></Field>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Location / Area">
                <Select value={f.location} onValueChange={(v) => setF({ ...f, location: v })}>
                  <SelectTrigger data-testid="pf-location" className="rounded-xl"><SelectValue placeholder="Select area" /></SelectTrigger>
                  <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.name}>{l.name} <span className="text-xs text-stone-400">· {l.city}</span></SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Contact number"><Input data-testid="pf-contact" value={f.contact} onChange={set("contact")} className="rounded-xl" /></Field>
              <Field label="Address"><Input value={f.address} onChange={set("address")} className="rounded-xl" /></Field>
              <Field label="City"><Input value={f.city} onChange={set("city")} className="rounded-xl" /></Field>
              <Field label="District"><Input value={f.district} onChange={set("district")} className="rounded-xl" /></Field>
              <Field label="State"><Input value={f.state} onChange={set("state")} className="rounded-xl" /></Field>
              <Field label="Latitude (optional, for map)"><Input data-testid="pf-lat" value={f.latitude} onChange={set("latitude")} placeholder="17.3850" className="rounded-xl" /></Field>
              <Field label="Longitude (optional, for map)"><Input data-testid="pf-lng" value={f.longitude} onChange={set("longitude")} placeholder="78.4867" className="rounded-xl" /></Field>
            </div>
            <Field label="Google Maps link (optional)"><Input value={f.maps_url} onChange={set("maps_url")} placeholder="https://maps.google.com/..." className="rounded-xl" /></Field>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h2 className="font-heading font-semibold">Capacity</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Field label="Maximum guests"><Input data-testid="pf-guests" type="number" min={1} value={f.max_guests} onChange={set("max_guests")} className="rounded-xl" /></Field>
              <Field label="Seating capacity"><Input data-testid="pf-seating" type="number" min={0} value={f.seating_capacity} onChange={set("seating_capacity")} className="rounded-xl" /></Field>
              <Field label="Dining capacity"><Input data-testid="pf-dining" type="number" min={0} value={f.dining_capacity} onChange={set("dining_capacity")} className="rounded-xl" /></Field>
            </div>
            <p className="text-xs text-stone-400">Seating & dining capacity are most relevant for banquet and function halls.</p>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <h2 className="font-heading font-semibold">Pricing (₹)</h2>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Field label="Starting price"><Input data-testid="pf-price" type="number" value={f.price_starting} onChange={set("price_starting")} className="rounded-xl" /></Field>
              <Field label="Weekday price"><Input type="number" value={f.price_weekday} onChange={set("price_weekday")} className="rounded-xl" /></Field>
              <Field label="Weekend price"><Input type="number" value={f.price_weekend} onChange={set("price_weekend")} className="rounded-xl" /></Field>
              <Field label="Holiday price"><Input type="number" value={f.price_holiday} onChange={set("price_holiday")} className="rounded-xl" /></Field>
            </div>
            {f.category === "Farmhouses" && (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <Field label="Day booking price"><Input type="number" value={f.price_dayout} onChange={set("price_dayout")} className="rounded-xl" /></Field>
                <Field label="Overnight price"><Input type="number" value={f.price_overnight} onChange={set("price_overnight")} className="rounded-xl" /></Field>
              </div>
            )}
            {f.category === "Resorts" && (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <Field label="Room price"><Input type="number" value={f.price_overnight} onChange={set("price_overnight")} className="rounded-xl" /></Field>
                <Field label="Event price"><Input type="number" value={f.price_dayout} onChange={set("price_dayout")} className="rounded-xl" /></Field>
              </div>
            )}
            {isHall && (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <Field label="Event price"><Input type="number" value={f.price_overnight} onChange={set("price_overnight")} className="rounded-xl" /></Field>
              </div>
            )}
          </div>
        )}

        {step === 5 && (
          <div>
            <h2 className="mb-1 font-heading font-semibold">Amenities</h2>
            <p className="mb-4 text-xs text-stone-400">Showing amenities relevant to {f.category || "your category"}.</p>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              {amenityList.map((a) => (
                <label key={a} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={f.amenities.includes(a)} onCheckedChange={() => toggleAmenity(a)} data-testid={`pf-amenity-${a}`} /> {a}
                </label>
              ))}
            </div>
          </div>
        )}

        {step === 6 && (
          <div className="space-y-4">
            <h2 className="font-heading font-semibold">Rules & Timings</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {["smoking", "pets", "parties"].map((k) => (
                <Field key={k} label={k[0].toUpperCase() + k.slice(1)}>
                  <Select value={f.rules[k]} onValueChange={setRule(k)}><SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Allowed">Allowed</SelectItem><SelectItem value="Not Allowed">Not Allowed</SelectItem></SelectContent></Select>
                </Field>
              ))}
              <Field label="Alcohol policy"><Input value={f.rules.alcohol || ""} onChange={(e) => setRule("alcohol")(e.target.value)} className="rounded-xl" /></Field>
              <Field label="Check-in time"><Input value={f.checkin} onChange={set("checkin")} className="rounded-xl" /></Field>
              <Field label="Check-out time"><Input value={f.checkout} onChange={set("checkout")} className="rounded-xl" /></Field>
            </div>
          </div>
        )}

        {step === 7 && (
          <div>
            <h2 className="mb-4 font-heading font-semibold">Photos</h2>
            <ImageUploader images={f.images} onChange={(imgs) => setF({ ...f, images: imgs })} />
          </div>
        )}

        {step === 8 && (
          <div className="space-y-4">
            <h2 className="font-heading font-semibold">Preview</h2>
            {f.images[0] && <img src={fileUrl(f.images[0])} alt="" className="h-44 w-full rounded-xl object-cover" />}
            <div>
              <span className="rounded-full bg-accent px-2.5 py-0.5 text-xs font-medium text-primary">{f.category}</span>
              <p className="mt-2 font-heading text-xl font-bold">{f.name || "Property name"}</p>
              <p className="flex items-center gap-1 text-sm text-stone-500"><MapPin size={14} weight="fill" className="text-primary" /> {f.location}, {f.city}</p>
              <p className="mt-1 font-heading text-lg font-bold text-primary">{formatINR(f.price_starting || 0)} onwards</p>
            </div>
            <p className="text-sm text-stone-600">{f.description}</p>
            <div className="flex flex-wrap gap-2">{f.amenities.map((a) => <span key={a} className="rounded-full bg-stone-100 px-2.5 py-0.5 text-xs">{a}</span>)}</div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="rounded-xl bg-stone-50 p-3"><p className="text-xs text-stone-400">Max Guests</p><p className="font-semibold">{f.max_guests}</p></div>
              <div className="rounded-xl bg-stone-50 p-3"><p className="text-xs text-stone-400">Seating</p><p className="font-semibold">{f.seating_capacity || "—"}</p></div>
              <div className="rounded-xl bg-stone-50 p-3"><p className="text-xs text-stone-400">Dining</p><p className="font-semibold">{f.dining_capacity || "—"}</p></div>
            </div>
            <div><p className="mb-2 text-sm font-medium">Location preview</p><MapView latitude={f.latitude === "" ? null : Number(f.latitude)} longitude={f.longitude === "" ? null : Number(f.longitude)} address={`${f.address} ${f.location} ${f.city}`} name={f.name} city={f.city} /></div>
          </div>
        )}

        {step === 9 && (
          <div className="py-4 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-accent"><Check size={30} weight="bold" className="text-primary" /></div>
            <h2 className="font-heading text-xl font-semibold">Ready to {editing ? "update" : "submit"}</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-stone-500">{editing ? "Save your changes." : "Your property will be sent to the Avinya admin team for approval. Once approved, it goes live for customers."}</p>
            <Button data-testid="pf-submit" onClick={submit} disabled={saving} className="mt-6 rounded-xl bg-primary px-10 py-6 text-base hover:bg-green-900">
              {saving ? "Saving..." : editing ? "Update Property" : "Submit for Approval"}
            </Button>
          </div>
        )}

        {step < 9 && (
          <div className="mt-8 flex items-center justify-between border-t border-stone-100 pt-5">
            <Button variant="outline" onClick={back} disabled={step === 1} className="gap-1 rounded-xl"><ArrowLeft size={16} /> Back</Button>
            <span className="text-xs text-stone-400">Step {step} of 9</span>
            <Button data-testid="pf-next" onClick={next} className="gap-1 rounded-xl bg-primary hover:bg-green-900">Next <ArrowRight size={16} /></Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
